<template>
  <Page v-for="(pageTransports, pageIndex) in transportPages" :key="pageIndex">
    <div class="banner-container" :style="{ backgroundColor: '#bc141a' }">
      <span class="study-name">{{ study.name }}</span>
      <span class="page-title">Overview</span>
    </div>

    <div class="data-container">
      <HeaderDataItem icon="icon-calendar" :title="$t('shortExportHeadlines.studyDate')">
        <template #value>{{ date }}</template>
      </HeaderDataItem>

      <div class="divider" />

      <HeaderDataItem icon="icon-clock" :title="$t('shortExportHeadlines.studyTime')">
        <template #value>{{ travelTimeType }} {{ time }}</template>
      </HeaderDataItem>

      <div class="divider" />

      <HeaderDataItem icon="icon-directions" :title="$t('shortExportHeadlines.routeType')">
        <template #value>{{ travelType }}</template>
      </HeaderDataItem>

      <div class="divider" />

      <HeaderDataItem icon="icon-bus" :title="$t('shortExportHeadlines.calculationBasis')">
        <template #value>
          <span v-for="(text, i) in calculationBasis" :key="i">{{ text }}</span>
        </template>
      </HeaderDataItem>

      <div :class="{ divider: study.withWalkingTimes && study.maximum_travel_time_enabled !== false }" />

      <HeaderDataItem
        v-if="study.withWalkingTimes && study.maximum_travel_time_enabled !== false"
        icon="icon-clock"
        :title="$t('shortExportHeadlines.travelTimeLimits')"
      >
        <template #value>
          <span v-for="(text, i) in travelTimeLimits" :key="i">{{ text }}</span>
        </template>
      </HeaderDataItem>
      <div v-else />

      <!-- second row -->

      <HeaderDataItem icon="icon-people" :title="$t('shortExportHeadlines.totalEmployees', study.collaborators_total)">
        <template #value>{{ study.collaborators_total }}</template>
      </HeaderDataItem>

      <div class="divider" />

      <HeaderDataItem icon="icon-people" :title="$t('shortExportHeadlines.accountedEmployees', study.collaborators_recognized)">
        <template #value>{{ study.collaborators_recognized }}</template>
      </HeaderDataItem>

      <div class="divider" />

      <HeaderDataItem icon="icon-pin" :title="$t('shortExportHeadlines.unrecognizedAddresses', study.unrecognized_addresses)">
        <template #value>{{ study.unrecognized_addresses }}</template>
      </HeaderDataItem>

      <div class="divider" />

      <HeaderDataItem icon="icon-zone" :title="$t('shortExportHeadlines.offLimitAddresses', study.off_limit_addresses)">
        <template #value>{{ study.off_limit_addresses }}</template>
      </HeaderDataItem>

      <div class="divider" />

      <HeaderDataItem icon="icon-cross" :title="$t('shortExportHeadlines.excludedAddresses', study.excluded_addresses)">
        <template #value>{{ study.excluded_addresses }}</template>
      </HeaderDataItem>
    </div>

    <table class="analysis-table">
      <thead>
        <tr class="section-header-row">
          <th class="row-label-cell" />

          <th v-for="transportType in pageTransports" :key="transportType" colspan="4" class="section-header">
            {{ getTransportLabel(transportType) }}
          </th>
        </tr>

        <tr class="employee-count-row">
          <td class="row-label-cell" />

          <td v-for="transportType in pageTransports" :key="transportType" colspan="4" class="employee-count">
            {{
              $t('collaboratorsIncludedInResults', analysisData[transportType].filteredCollaboratorsCount, {
                count: analysisData[transportType].filteredCollaboratorsCount,
              })
            }}
          </td>
        </tr>

        <tr class="col-header-row">
          <th class="row-label-cell" />

          <template v-for="transportType in pageTransports" :key="transportType">
            <th class="col-header">{{ $t('shortExportHeadlines.travelTime') }}</th>
            <th class="col-header">{{ $t('shortExportHeadlines.evolution') }}</th>
            <th class="col-header">{{ $t('shortExportHeadlines.carbonFootprint') }}</th>
            <th class="col-header">{{ $t('shortExportHeadlines.impactOnEmployees') }}</th>
          </template>
        </tr>
      </thead>

      <tbody>
        <!-- Current location -->
        <tr class="data-row">
          <td class="row-label">
            <Site :data="{ caption: $t('actualSites', exportActualSites.length) }" />
          </td>

          <template v-for="transportType in pageTransports" :key="transportType">
            <td class="cell cell-center">
              <RawData :data="{ value: analysisData[transportType].averageTime, sub: ' min' }" />
            </td>
            <td class="cell cell-borderless" />
            <td class="cell cell-borderless" />
            <td class="cell cell-borderless cell-right-border" />
          </template>
        </tr>

        <!-- Target locations -->
        <tr v-for="(site, siteIndex) in targetSites" :key="site.id" class="data-row">
          <td class="row-label">
            <Site :data="{ caption: site.name, index: site.index + 1, image: site.picture_path }" />
          </td>

          <template v-for="transportType in pageTransports" :key="transportType">
            <td class="cell cell-center">
              <RawData :data="{ value: analysisData[transportType].sites[siteIndex].details.averageTime, sub: ' min' }" />
            </td>
            <td class="cell cell-center">
              <RawData :data="getSiteEvolutionData(transportType, siteIndex)" />
            </td>
            <td class="cell cell-center">
              <RawData :data="getSiteEnvironmentalImpactData(transportType, siteIndex)" />
            </td>
            <td class="cell cell-center">
              <Impact
                :pie="{ radius: 16, innerRadius: 10 }"
                :count="{
                  reduction: analysisData[transportType].sites[siteIndex].details.impact.decay.count,
                  increase: analysisData[transportType].sites[siteIndex].details.impact.improvement.count,
                  sensibleCase: analysisData[transportType].sites[siteIndex].details.impact.sensibleCaseDecay.count,
                }"
              />
            </td>
          </template>
        </tr>
      </tbody>
    </table>
  </Page>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Report/BasePage';
import HeaderDataItem from '@/components/Report/ShortExport/HeaderDataItem';
import RawData from '@/components/Table/RawData';
import Impact from '@/components/Report/ShortExport/Impact';
import Site from '@/components/Report/ShortExport/Site';
import ComputedData from '@/pure/ComputedData';
import assetProvider from '@/pure/assetProvider';

export default {
  components: { Impact, RawData, HeaderDataItem, Page, Site },

  data() {
    return {
      transportsOrder: ['public', 'private', 'optimized', 'custom'],
    };
  },

  computed: {
    ...mapGetters(['study', 'currentExportLayout', 'globalFilters', 'exportActualSites', 'exportTargetSites']),

    date() {
      if (!this.study) return;

      if (typeof this.study.date === 'number') {
        // legacy
        return this.$d(new Date(this.study.date * 1000), 'date');
      }

      return this.$d(new Date(this.study.date), 'date');
    },

    travelTimeType() {
      if (!this.study) return;

      return this.study.travel_time_type === 1 ? `${this.$t('arrivalAt')} ` : `${this.$t('departureAt')} `;
    },

    time() {
      if (!this.study) return;

      let dateTime = new Date(this.study.date);
      if (typeof this.study.date === 'number') {
        // legacy
        dateTime = new Date(this.study.date * 1000);
      }

      return `${dateTime.getHours()}h${dateTime.getMinutes() > 0 ? dateTime.getMinutes() : ''}`;
    },

    travelTimeLimits() {
      if (!this.study) return;
      if (!this.study.withWalkingTimes || this.study.maximum_travel_time_enabled === false) return;

      let travelTimeLimits = [];

      if (this.study.public_transport_type !== 0) {
        travelTimeLimits.push(this.$t('transportMode.public') + ' : ' + this.study.public_maximum_travel_time + 'min');
      }

      if (this.study.individual_transport_type !== 0) {
        travelTimeLimits.push(this.$t('transportMode.private') + ' : ' + this.study.private_maximum_travel_time + 'min');
      }

      return travelTimeLimits;
    },

    travelType() {
      if (!this.study) return;

      return this.study.travel_type === 1 ? this.$t('routeOneWay') : this.$t('routeReturn');
    },

    calculationBasis() {
      if (!this.study) return;

      const result = [];

      if (this.study.individual_transport_only === false) {
        result.push(this.$t('transportMode.public') + (this.study.futur_transport_type !== 0 ? ` ${this.$t('transportMode.actuals')}` : ''));

        if (this.study.futur_transport_type !== 0) {
          const futureTransportTypeMap = {
            1: 'transportMode.reach2025',
            2: 'transportMode.reach2030',
            3: 'transportMode.reach2027',
            4: 'transportMode.reach2031',
          };

          result.push(this.$t('transportMode.public') + ' ' + this.$t(futureTransportTypeMap[this.study.futur_transport_type]));
        }
      }

      result.push(this.$t('transportMode.private'));

      return result;
    },

    activeTransports() {
      return this.transportsOrder.filter(transport => {
        if (transport === 'public') return this.currentExportLayout.public_transport_type !== 0;
        if (transport === 'private') return this.currentExportLayout.individual_transport_type !== 0;
        if (transport === 'optimized') return this.currentExportLayout.optimized_transport_enabled;
        if (transport === 'custom') return this.currentExportLayout.custom_transport_enabled;
      });
    },

    transportPages() {
      const pages = [];

      for (let i = 0; i < this.activeTransports.length; i += 2) {
        pages.push(this.activeTransports.slice(i, i + 2));
      }

      return pages.length ? pages : [[]];
    },

    targetSites() {
      if (!this.analysisData || !this.activeTransports.length) return [];

      const sites = this.analysisData[this.activeTransports[0]]?.sites ?? [];

      sites.forEach(site => {
        site.picture_path = site.picture_path || assetProvider.placeHolderSite;
      });

      return sites;
    },

    analysisData() {
      if (!this.study) return;

      const targetSites = this.exportTargetSites.map(site => site.id);

      const computedData = new ComputedData();

      const data = {};

      for (const currentMatchingTime of this.activeTransports) {
        const collaborators = this.getFilteredCollaborators(currentMatchingTime, targetSites);
        data[currentMatchingTime] = computedData.reduceData(collaborators, this.study, { currentMatchingTime, targetSites });
      }

      return data;
    },
  },

  created() {
    const exportId = this.currentExportLayout.public_id;

    this.getExportSiteMap({ id: exportId }).then(({ url }) => (this.sitesMapUrl = url));
    this.getExportSiteMapCollaborator({ id: exportId }).then(({ url }) => (this.collaboratorsMapUrl = url));
  },

  methods: {
    ...mapActions(['getExportSiteMap', 'getExportSiteMapCollaborator']),

    buildTrendData(diff, sub) {
      return {
        trend: diff === 0 ? 0 : diff < 0 ? 'down' : 'up',
        className: diff <= 0 ? 'green' : diff > 10 && diff <= 30 ? 'orange' : diff > 30 ? 'red' : null,
        value: Math.abs(diff),
        sub,
      };
    },

    getSiteEvolutionData(transportType, siteIndex) {
      const diff = this.analysisData[transportType].sites[siteIndex].details.averageTimeDiff;

      return this.buildTrendData(diff, ' min');
    },

    getSiteEnvironmentalImpactData(transportType, siteIndex) {
      const actualImpact = this.analysisData[transportType].environmentalImpact;
      const targetImpact = this.analysisData[transportType].sites[siteIndex].details.environmentalImpact;
      const diffPct = actualImpact !== 0 ? Math.round(((targetImpact - actualImpact) / actualImpact) * 100) : 0;

      return this.buildTrendData(diffPct, ' %');
    },

    getTransportLabel(transportType) {
      const labelForType = {
        public: this.$t('transportMode.public'),
        private: this.$t('transportMode.private'),
        optimized: this.$t('transportMode.optimized'),
        custom: this.$t('transportMode.custom'),
      };

      return labelForType[transportType];
    },

    getFilteredCollaborators(currentTransportFilter, targetSiteIds) {
      let collaborators = this.study.collaborators.filter(item => item.address != null && item.sites && item.sites.length > 0);

      if (!this.study.withWalkingTimes || this.study.maximum_travel_time_enabled === false) return collaborators;

      let maximumTravelTimeForCurrentTransport = this.study[currentTransportFilter + '_maximum_travel_time'];

      return collaborators.filter(collaborator => {
        let collaboratorTransport;
        if (currentTransportFilter === 'custom') {
          collaboratorTransport = collaborator.details.transportType;
          maximumTravelTimeForCurrentTransport = this.study[collaboratorTransport + '_maximum_travel_time'];
        }

        const allTargetSitesTooFar = targetSiteIds.every(targetSiteId => {
          const collaboratorTargetSite = collaborator.sites.find(site => site.id === targetSiteId);
          if (!collaboratorTargetSite) return true;

          if (currentTransportFilter === 'optimized') {
            collaboratorTransport = collaboratorTargetSite.optimizedSite;
            maximumTravelTimeForCurrentTransport = this.study[collaboratorTransport + '_maximum_travel_time'];
          }

          const collaboratorTimeForCurrentTransport = collaboratorTargetSite.times[0][collaboratorTransport || currentTransportFilter] || 0;

          return maximumTravelTimeForCurrentTransport < collaboratorTimeForCurrentTransport;
        });

        return !allTargetSitesTooFar;
      });
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';

.page {
  padding: 32px 32px 56px;
  box-sizing: border-box;
  position: relative;

  .banner-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 28px;
    display: flex;
    align-items: center;
    color: white;
    box-sizing: border-box;
    padding: 0 96px;

    .study-name {
      font-size: 16px;
    }

    .page-title {
      font-size: 14px;
      margin: 0 0 0 auto;
    }
  }

  .data-container {
    display: grid;
    grid-template-columns: repeat(4, 1fr 1px) 1fr;
    gap: 8px;
    width: 100%;

    .divider {
      height: 50px;
      background: colors.$dark-beige-color;
      align-self: center;
    }
  }

  .analysis-table {
    margin-top: 16px;
    font-size: 14px;
    width: 100%;
    border-collapse: collapse;
    color: #333;

    .row-label-cell {
      border: none;
    }

    .section-header {
      font-size: 16px;
      font-weight: 700;
      text-align: center;
      padding: 8px;
      border: 1px solid colors.$clear-border-color;
      background: colors.$beige-color;
    }

    .employee-count {
      text-align: center;
      font-size: 12px;
      color: #666;
      padding: 4px;
      border: 1px solid colors.$clear-border-color;
      border-top: none;
    }

    .col-header {
      font-size: 12px;
      font-weight: 400;
      text-align: center;
      padding: 4px;
      border: 1px solid colors.$clear-border-color;
      border-top: none;
      white-space: nowrap;
    }

    .data-row {
      border-bottom: 1px solid colors.$clear-border-color;
    }

    .row-label {
      font-size: 12px;
      padding: 4px;
      white-space: nowrap;
      font-weight: 700;
      border: 1px solid colors.$clear-border-color;
      vertical-align: middle;
    }

    .cell {
      padding: 4px;
      border-right: 1px solid colors.$clear-border-color;
      vertical-align: middle;
    }

    .cell-center {
      > div {
        justify-content: center;
      }
    }

    .cell-borderless {
      border: none;
    }

    .cell-right-border {
      border-right: 1px solid colors.$clear-border-color;
    }
  }
}
</style>
